/**
 * Path to Victory Slide
 * Timeline from Now → Future
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import {
  DataSlide,
  TimelineContainer,
  TimelineLabel,
  TimelineTrack,
  TimelineFill,
  TimelineStops,
  TimelineStop,
  TimelineLabels,
  TimelineDot,
  TimelineStopTitle,
  TimelineStopDetail,
  TimelineBulletList,
  TimelineBullet,
  SlideAlertPanel,
  SlideAlertBox,
  SlideSectionTitle,
  DarkInsightEmphasis,
} from '@components/press/pitch-deck/slide-primitives';

/* Centre dots vertically on the 4px track:
   gap 8px + track 4px → stops top at track_top+12 by default.
   Dot is 26px (20+2×3 border), centre at 13px.
   Need stops_top + 13 = track_top + 2  →  margin-top = -23px. */
const CentredStops = styled(TimelineStops)`
  margin-top: -23px;
`;

/* Shorten the track so it spans between the centres of the first and
   last stops.  Stops use padding: 0 4% and three equal flex-1 columns,
   so each stop centre is 4% + (92%/6) ≈ 19.3% from the edge. */
const ShorterTrack = styled(TimelineTrack)`
  margin: 0 calc(4% + (100% - 8%) / 6);
  width: auto;
`;

/* Labels centred over the first / last stop columns (3-col flex). */
const AlignedLabels = styled(TimelineLabels)`
  padding: 0 4%;
`;

const LabelSlot = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

/* Larger bullet text with ring instead of filled dot */
const RingBullet = styled(TimelineBullet)`
  font-size: clamp(${typography.fontSize.sm}, 1.4vw, ${typography.fontSize.base});

  &::before {
    background: transparent;
    border: 1.5px solid ${colors.highlight.light};
    width: 6px;
    height: 6px;
    top: 9px;
  }
`;

/* Dark backdrop matching the alert-box gradient, wrapping title + grid. */
const AlertBackdrop = styled.div`
  width: 100%;
  background: linear-gradient(135deg, ${colors.highlight.dark} 0%, ${colors.highlight.light} 100%);
  border-radius: ${borderRadius.xl};
  padding: ${spacing['2xl']} ${spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};

  /* Strip outer border, restyle inner ::after as coral-light inset */
  & > * > div {
    border: none;
    &::after {
      border-color: ${colors.accent.coral.light}40;
      display: block;
    }
  }

  /* Remove brand color from emphasis — keep semibold, use white */
  ${DarkInsightEmphasis} {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const WhiteSectionTitle = styled(SlideSectionTitle)`
  color: #fff;
  padding-bottom: ${spacing.md};
`;

const ACCENT = {
  short: colors.accent.teal.exxtraDark,
  medium: colors.accent.blue.exxtraDark,
  long: colors.accent.purple.exxtraDark,
};

export const PathToVictorySlide: React.FC = () => (
  <DataSlide title="Path to Victory" subtitle="PreSeed Goal to Reach 10% of Beachhead Market with $5M+ ARR">

    <TimelineContainer>
      <AlignedLabels>
        <LabelSlot><TimelineLabel>Now</TimelineLabel></LabelSlot>
        <LabelSlot />
        <LabelSlot><TimelineLabel>Future</TimelineLabel></LabelSlot>
      </AlignedLabels>
      <ShorterTrack>
        <TimelineFill />
      </ShorterTrack>
      <CentredStops>

        <TimelineStop $color={ACCENT.short}>
          <TimelineDot $color={ACCENT.short} />
          <TimelineStopTitle $color={ACCENT.short}>
            Viral Meet the Moments <br />Customized for Beachhead Moms
          </TimelineStopTitle>
          <TimelineStopDetail>
            <TimelineBulletList>
            <RingBullet>Push Button Date Nights</RingBullet>
              <RingBullet>Vaccine Summaries</RingBullet>
              <RingBullet>Kid Activity News Feeds</RingBullet>
              <RingBullet>Kid School Calendar Sync</RingBullet>
              <RingBullet>Partner Task Sharing</RingBullet>
              <RingBullet>...be dynamic and responsive</RingBullet>
            </TimelineBulletList>
          </TimelineStopDetail>
        </TimelineStop>

        <TimelineStop $color={ACCENT.medium}>
          <TimelineDot $color={ACCENT.medium} />
          <TimelineStopTitle $color={ACCENT.medium}>
            Complete the Coverage <br />The 5 Mile March
          </TimelineStopTitle>
          <TimelineStopDetail>
            <TimelineBulletList>
              <RingBullet>Expand to covercore User Goals for Moms</RingBullet>
              <RingBullet>Identify, custom build, reach, and onboard new Moms</RingBullet>
              <RingBullet>Systematic Growth will require new techniques, but old principles still apply</RingBullet>
            </TimelineBulletList>
          </TimelineStopDetail>
        </TimelineStop>

        <TimelineStop $color={ACCENT.long}>
          <TimelineDot $color={ACCENT.long} />
          <TimelineStopTitle $color={ACCENT.long}>
            Far Into the Future<br />Market Maker
          </TimelineStopTitle>
          <TimelineStopDetail>
            <TimelineBulletList>
              <RingBullet>Moms are the most powerful consumer group and can open additional market opportunities</RingBullet>
            </TimelineBulletList>
          </TimelineStopDetail>
        </TimelineStop>

      </CentredStops>
    </TimelineContainer>

    <AlertBackdrop>
      <WhiteSectionTitle>Preseed Competitive Edges to Test</WhiteSectionTitle>
      <SlideAlertPanel columns={2}>
        <SlideAlertBox headline="Proactive v Reactive">
          Predictive modeling for standard challenges in <DarkInsightEmphasis>car maintenance, shoes, spring cleaning, ...</DarkInsightEmphasis> preemptively planned for with AI.
        </SlideAlertBox>

        <SlideAlertBox headline="Niche Household Modeling">
          <DarkInsightEmphasis>Family Planning, Vet visits, Education Finances</DarkInsightEmphasis> niche mathematical models, grounding expectations and allowing for top of funnel planning.
        </SlideAlertBox>

        <SlideAlertBox headline="Cross Household Communication">
          Syncing actions between <DarkInsightEmphasis>Google, Outlook, and Text</DarkInsightEmphasis> across people and tools, with kindness.
        </SlideAlertBox>

        <SlideAlertBox headline="Trust">
          A <DarkInsightEmphasis>closed Ad free system</DarkInsightEmphasis> keeping your data inside your household
        </SlideAlertBox>
      </SlideAlertPanel>
    </AlertBackdrop>

  </DataSlide>
);
